import { useQuery } from '@apollo/client';
import { Link, useParams } from 'react-router-dom';
import { TypoBody, TypoFootnote, TypoHeading2 } from '@/components/Typography.tsx';
import { ArrowLeft2, PSATIllustration } from '@/components/Icons.tsx';
import Loader from '@/components/Loader.tsx';
import Button from '@/components/Button.tsx';
import ScoringGuideline from '@/components/ScoringGuideline.tsx';
import useUser from '@/hooks/useUser.ts';
import STUDY_CENTER_PAGES from '@/graphql/queries/study-center-pages.graphql';

import AccessWarning from './AccessWarning.tsx';
import { getPageWithCalculations, StudyCenterPageProps } from './common.ts';

const StudyCenterGroup = () => {
  const { groupId = '' } = useParams();
  const { user, loading } = useUser();

  if (loading) return <Loader />;
  if (!user.hasStudycenter) return <AccessWarning />;

  return (
    <>
      <div className="flex items-center justify-between px-10 pt-6 pb-4">
        <TypoHeading2 className="text-neutral-text-strong font-medium">
          Study Center
        </TypoHeading2>
        <ScoringGuideline title="About Study Center" />
      </div>
      <StudyCenterPages groupId={Number(groupId)} />
    </>
  );
};

const StudyCenterPages = ({ groupId }:{ groupId: number }) => {
  const { groupName, pages, loading } = useStudyCenterPages(groupId);

  if (loading) return <Loader />;

  return (
    <div className="bg-neutral2 text-neutral2-text-soft rounded p-10 pb-8">
      <div className="flex items-center gap-x-3 py-2 mb-8">
        <Link
          to="/study-center"
          className="flex flex-row items-center rounded px-4 py-2 gap-x-2 bg-shade-5 whitespace-nowrap"
        >
          <ArrowLeft2 width={16} height={16} /><span className="text-common-white text-callout">Back</span>
        </Link>
        <strong className="font-primary text-body py-2">{groupName}</strong>
      </div>

      {Boolean(pages?.length) && (
        <div>
          <div className="flex gap-2 items-center">
            <TypoFootnote className="text-neutral2-text-strong font-semibold ml-24 flex-1 flex-shrink-0 pl-1">
              QUESTION TYPE
            </TypoFootnote>
            <TypoFootnote className="text-neutral2-text-strong font-semibold min-w-0 w-16 text-center flex-shrink-0">
              TOTAL POINTS
            </TypoFootnote>
            <TypoFootnote className="text-neutral2-text-strong font-semibold min-w-0 w-16 text-center flex-shrink-0">
              YOUR POINTS
            </TypoFootnote>
            <span className="w-32 -ml-1.5" />
          </div>
          {pages.map((page: StudyCenterPageProps & { currentPoints: number }) =>
            <PageItem key={page.studycenterPageId} {...page} />)}
        </div>
      )}
    </div>
  );
};

const PageItem = ({
  studycenterPageId,
  category,
  projectedPoints,
  currentPoints
}: StudyCenterPageProps & { currentPoints: number }) => (
  <div className="flex justify-between gap-2 px-4 rounded-xl bg-neutral items-center py-2 mt-2">
    <PSATIllustration width="72" height="72" />
    <TypoBody className="flex-1 text-neutral-text-strong font-semibold ml-1">{category.name}</TypoBody>
    <TypoHeading2 className="font-bold text-neutral-text-strong min-w-0 w-16 text-center flex-shrink-0">
      {projectedPoints}
    </TypoHeading2>
    <TypoHeading2 className="font-bold text-neutral-text-strong min-w-0 w-16 text-center flex-shrink-0">
      {currentPoints}
    </TypoHeading2>
    <Link to={`/study-center/${studycenterPageId}`}>
      <Button withArrow className="ml-1">Adjust</Button>
    </Link>
  </div>
);

const useStudyCenterPages = (groupId: number) => {
  const { data, loading } = useQuery(STUDY_CENTER_PAGES);
  const all = (data?.studyCenterPages ?? []);

  const grouped = all.filter((page: StudyCenterPageProps) => Number(page.group?.studycenterGroupId) === groupId);
  const ungrouped = all.filter((page: StudyCenterPageProps) => !page.group);
  const list = groupId ? grouped : ungrouped;

  const formatted = list.map((page: StudyCenterPageProps) => getPageWithCalculations(page));

  return { loading, pages: formatted, groupName: grouped[0]?.group?.name ?? 'Untitled' };
};


export default StudyCenterGroup;
